import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { getCategoryConfig } from '../../config/milestoneCategories'
import { createLifeEvent } from '../../services/api'

function MilestoneWizard({ category = 'career' }) {
  const navigate = useNavigate()
  const config = getCategoryConfig(category)

  const [currentStep, setCurrentStep] = useState(1)
  const [formData, setFormData] = useState({
    type: '',
    title: '',
    month: '',
    year: '',
    location: '',
    customFields: {},
    story: ''
  })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  const progressLabels = ['', 'What happened?', 'Give it a title', 'When & where', 'Tell the story', 'Complete!']

  // Month and year options for dropdowns
  const months = [
    { value: '01', label: 'January' },
    { value: '02', label: 'February' },
    { value: '03', label: 'March' },
    { value: '04', label: 'April' },
    { value: '05', label: 'May' },
    { value: '06', label: 'June' },
    { value: '07', label: 'July' },
    { value: '08', label: 'August' },
    { value: '09', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' }
  ]

  // Generate years from 1920 to current year
  const currentYear = new Date().getFullYear()
  const years = []
  for (let year = currentYear; year >= 1920; year--) {
    years.push(year)
  }

  const handleBack = () => {
    if (currentStep === 1) {
      navigate('/my-life-v2')
    } else {
      setCurrentStep(currentStep - 1)
    }
  }

  const handleNext = () => {
    setCurrentStep(currentStep + 1)
    window.scrollTo(0, 0)
  }

  const handleSave = async () => {
    setSaving(true)
    setError(null)

    try {
      // Convert month + year to YYYY-MM-DD format
      const formattedDate = `${formData.year}-${formData.month}-01`

      // Prepare the event data for the API
      const eventData = {
        title: formData.title,
        date: formattedDate,
        location: formData.location || '',
        category: config.name,
        template: 1, // Generic milestone template
        event_data: {
          category: category,
          milestoneType: formData.type,
          story: formData.story || '',
          ...formData.customFields
        }
      }

      const response = await createLifeEvent(eventData)

      setCurrentStep(5)
      window.scrollTo(0, 0)
    } catch (err) {
      console.error('Failed to save milestone:', err)
      setError(err.message || 'Failed to save milestone. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  const handleSelectType = (typeId) => {
    setFormData({ ...formData, type: typeId })
  }

  const handleCustomFieldChange = (key, value) => {
    setFormData({
      ...formData,
      customFields: {
        ...formData.customFields,
        [key]: value
      }
    })
  }

  const handleTitleExampleClick = (example) => {
    setFormData({ ...formData, title: example })
  }

  const canProceedStep1 = formData.type !== ''
  const canProceedStep2 = formData.title.trim().length >= 3
  const canProceedStep3 = formData.month !== '' && formData.year !== ''

  const progressPercent = currentStep === 5 ? 100 : (currentStep / 4) * 100

  return (
    <div className="bg-[#F5F5DC] min-h-screen">
      {/* Wizard Header */}
      <header className="bg-[#556B2F] px-6 py-5 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={handleBack}
            className="w-12 h-12 rounded-xl bg-white bg-opacity-20 text-white text-2xl flex items-center justify-center hover:bg-opacity-30 transition-all"
          >
            ←
          </button>
          <div>
            <div className="text-white text-xl font-bold">{config.headerTitle}</div>
            <div className="text-white text-sm opacity-80 flex items-center gap-2">
              <span className="bg-white bg-opacity-20 px-3 py-1 rounded-full">
                {config.emoji} {config.name}
              </span>
            </div>
          </div>
        </div>
        <button className="bg-white bg-opacity-20 text-white px-5 py-2 rounded-lg text-base font-semibold hover:bg-opacity-30 transition-all">
          ❓ Help
        </button>
      </header>

      {/* Progress Bar */}
      <div className="bg-white px-6 py-5 border-b border-gray-200">
        <div className="max-width-2xl mx-auto">
          <div className="flex justify-between mb-2">
            <span className="text-base font-semibold text-[#556B2F]">
              {currentStep === 5 ? 'Complete!' : `Step ${currentStep} of 4`}
            </span>
            <span className="text-sm text-[#666666]">{progressLabels[currentStep]}</span>
          </div>
          <div className="h-2.5 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-[#6B8E23] to-[#556B2F] rounded-full transition-all duration-400"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-2xl mx-auto px-6 py-8">
        {/* Step 1: Type Selection */}
        {currentStep === 1 && (
          <div className="bg-white rounded-3xl p-10 border border-gray-300 shadow-lg">
            <div className="text-5xl mb-3">{config.step1Emoji}</div>
            <h1 className="text-3xl font-bold text-[#333333] mb-2">
              {config.step1Title}
            </h1>
            <p className="text-lg text-[#666666] mb-8">
              Select the one that best describes your experience
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
              {config.types.map((type) => (
                <button
                  key={type.id}
                  onClick={() => handleSelectType(type.id)}
                  className={`p-6 border-3 rounded-2xl text-left transition-all ${
                    formData.type === type.id
                      ? 'border-[#556B2F] bg-[#E8F0E0]'
                      : 'border-gray-300 bg-white hover:border-[#6B8E23] hover:bg-[#FAFFF8]'
                  }`}
                >
                  <div className="text-4xl mb-2">{type.emoji}</div>
                  <div className="text-lg font-bold text-[#333333] mb-1">{type.title}</div>
                  <div className="text-sm text-[#666666]">{type.desc}</div>
                </button>
              ))}
            </div>

            <div className="flex justify-between pt-6 border-t border-gray-200">
              <button
                onClick={() => navigate('/my-life-v2')}
                className="px-8 py-4 text-lg font-semibold border-3 border-gray-300 text-[#666666] rounded-xl hover:bg-gray-50 transition-all"
              >
                ← Cancel
              </button>
              <button
                onClick={handleNext}
                disabled={!canProceedStep1}
                className={`px-8 py-4 text-lg font-semibold rounded-xl text-white transition-all ${
                  canProceedStep1
                    ? 'bg-[#556B2F] hover:opacity-90'
                    : 'bg-gray-300 cursor-not-allowed'
                }`}
              >
                Next →
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Title */}
        {currentStep === 2 && (
          <div className="bg-white rounded-3xl p-10 border border-gray-300 shadow-lg">
            <div className="text-5xl mb-3">✏️</div>
            <h1 className="text-3xl font-bold text-[#333333] mb-2">
              Give this milestone a title
            </h1>
            <p className="text-lg text-[#666666] mb-8">
              A short description of what happened
            </p>

            <div className="mb-8">
              <label className="block text-lg font-semibold text-[#333333] mb-3">
                What should we call this milestone? <span className="text-[#E65100]">*</span>
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder={config.titlePlaceholder}
                className="w-full px-5 py-4 text-xl border-3 border-gray-300 rounded-xl bg-gray-50 focus:outline-none focus:border-[#6B8E23] focus:bg-white transition-all"
              />
              <div className="text-sm text-[#666666] mt-2">
                💡 Be specific so you'll remember this moment
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                {config.titleExamples.map((example, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleTitleExampleClick(example)}
                    className="px-4 py-2 bg-[#FFF8E8] border border-[#F5D799] rounded-full text-sm text-[#8B7355] hover:bg-[#FFF3D9] transition-all"
                  >
                    {example}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex justify-between pt-6 border-t border-gray-200">
              <button
                onClick={handleBack}
                className="px-8 py-4 text-lg font-semibold border-3 border-gray-300 text-[#666666] rounded-xl hover:bg-gray-50 transition-all"
              >
                ← Back
              </button>
              <button
                onClick={handleNext}
                disabled={!canProceedStep2}
                className={`px-8 py-4 text-lg font-semibold rounded-xl text-white transition-all ${
                  canProceedStep2
                    ? 'bg-[#556B2F] hover:opacity-90'
                    : 'bg-gray-300 cursor-not-allowed'
                }`}
              >
                Next →
              </button>
            </div>
          </div>
        )}

        {/* Step 3: When & Where + Custom Fields */}
        {currentStep === 3 && (
          <div className="bg-white rounded-3xl p-10 border border-gray-300 shadow-lg">
            <div className="text-5xl mb-3">📅</div>
            <h1 className="text-3xl font-bold text-[#333333] mb-2">
              When and where did this happen?
            </h1>
            <p className="text-lg text-[#666666] mb-8">
              It's okay to be approximate!
            </p>

            <div className="space-y-5 mb-6">
              {/* Month and Year Selection */}
              <div>
                <label className="block text-lg font-semibold text-[#333333] mb-3">
                  When did this happen? <span className="text-[#E65100]">*</span>
                </label>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <select
                      value={formData.month}
                      onChange={(e) => setFormData({ ...formData, month: e.target.value })}
                      className="w-full px-5 py-4 text-xl border-3 border-gray-300 rounded-xl bg-gray-50 focus:outline-none focus:border-[#6B8E23] focus:bg-white transition-all cursor-pointer"
                    >
                      <option value="">Select Month</option>
                      {months.map((month) => (
                        <option key={month.value} value={month.value}>
                          {month.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <select
                      value={formData.year}
                      onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                      className="w-full px-5 py-4 text-xl border-3 border-gray-300 rounded-xl bg-gray-50 focus:outline-none focus:border-[#6B8E23] focus:bg-white transition-all cursor-pointer"
                    >
                      <option value="">Select Year</option>
                      {years.map((year) => (
                        <option key={year} value={year}>
                          {year}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="text-sm text-[#666666] mt-2">💡 Select the month and year this happened</div>
              </div>

              {/* Location */}
              <div>
                <label className="block text-lg font-semibold text-[#333333] mb-3">
                  Where did this happen?
                </label>
                <input
                  type="text"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  placeholder="e.g., San Francisco, CA"
                  className="w-full px-5 py-4 text-xl border-3 border-gray-300 rounded-xl bg-gray-50 focus:outline-none focus:border-[#6B8E23] focus:bg-white transition-all"
                />
              </div>
            </div>

            {/* Category-specific custom fields */}
            {config.customFields && config.customFields.length > 0 && (
              <div className="space-y-5 mb-8">
                {config.customFields.map((field) => (
                  <div key={field.key} className={field.fullWidth ? '' : 'md:col-span-1'}>
                    <label className="block text-lg font-semibold text-[#333333] mb-3">
                      {field.label}
                    </label>
                    <input
                      type="text"
                      value={formData.customFields[field.key] || ''}
                      onChange={(e) => handleCustomFieldChange(field.key, e.target.value)}
                      placeholder={field.placeholder}
                      className="w-full px-5 py-4 text-xl border-3 border-gray-300 rounded-xl bg-gray-50 focus:outline-none focus:border-[#6B8E23] focus:bg-white transition-all"
                    />
                  </div>
                ))}
              </div>
            )}

            <div className="flex justify-between pt-6 border-t border-gray-200">
              <button
                onClick={handleBack}
                className="px-8 py-4 text-lg font-semibold border-3 border-gray-300 text-[#666666] rounded-xl hover:bg-gray-50 transition-all"
              >
                ← Back
              </button>
              <button
                onClick={handleNext}
                disabled={!canProceedStep3}
                className={`px-8 py-4 text-lg font-semibold rounded-xl text-white transition-all ${
                  canProceedStep3
                    ? 'bg-[#556B2F] hover:opacity-90'
                    : 'bg-gray-300 cursor-not-allowed'
                }`}
              >
                Next →
              </button>
            </div>
          </div>
        )}

        {/* Step 4: Story (Optional) */}
        {currentStep === 4 && (
          <div className="bg-white rounded-3xl p-10 border border-gray-300 shadow-lg">
            <div className="text-5xl mb-3">📖</div>
            <h1 className="text-3xl font-bold text-[#333333] mb-2">
              Want to share the story?
            </h1>
            <p className="text-lg text-[#666666] mb-8">
              This is optional, but your family will love to read it!
            </p>

            {/* Error Message */}
            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
                <div className="flex items-start gap-3">
                  <span className="text-2xl">⚠️</span>
                  <div>
                    <div className="font-semibold text-red-800 mb-1">Error Saving Milestone</div>
                    <div className="text-sm text-red-600">{error}</div>
                  </div>
                </div>
              </div>
            )}

            <div className="mb-8">
              <label className="block text-lg font-semibold text-[#333333] mb-3">
                {config.storyLabel}
              </label>
              <textarea
                value={formData.story}
                onChange={(e) => setFormData({ ...formData, story: e.target.value })}
                placeholder={config.storyPlaceholder}
                rows="6"
                disabled={saving}
                className="w-full px-5 py-4 text-xl border-3 border-gray-300 rounded-xl bg-gray-50 focus:outline-none focus:border-[#6B8E23] focus:bg-white transition-all resize-vertical disabled:opacity-50 disabled:cursor-not-allowed"
              />
              <div className="text-sm text-[#666666] mt-2">
                💡 You can always add more details later
              </div>
            </div>

            <div className="flex justify-between pt-6 border-t border-gray-200">
              <button
                onClick={handleBack}
                disabled={saving}
                className="px-8 py-4 text-lg font-semibold border-3 border-gray-300 text-[#666666] rounded-xl hover:bg-gray-50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                ← Back
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className={`px-8 py-4 text-lg font-semibold rounded-xl text-white transition-all ${
                  saving
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-[#E65100] hover:opacity-90'
                }`}
              >
                {saving ? 'Saving...' : 'Save Milestone ✓'}
              </button>
            </div>

            {!saving && (
              <div className="text-center mt-4">
                <button
                  onClick={handleSave}
                  className="text-[#666666] text-base underline hover:text-[#333333]"
                >
                  Skip for now - I'll add the story later
                </button>
              </div>
            )}
          </div>
        )}

        {/* Step 5: Success */}
        {currentStep === 5 && (
          <div className="bg-white rounded-3xl p-10 border border-gray-300 shadow-lg text-center">
            <div className="text-7xl mb-5">🎉</div>
            <h1 className="text-4xl font-bold text-[#556B2F] mb-2">
              Milestone Saved!
            </h1>
            <p className="text-lg text-[#666666] mb-8">
              {config.successMessage}
            </p>

            <div className="bg-[#F5F5F0] rounded-xl p-5 text-left mb-8">
              <div className="text-xs text-[#666666] uppercase tracking-wide mb-2">
                YOUR MILESTONE
              </div>
              <div className="text-xl font-bold text-[#333333] mb-2">
                {config.emoji} {formData.title || config.titlePlaceholder.replace('e.g., ', '')}
              </div>
              <div className="text-base text-[#666666]">
                📅 {formData.month && formData.year ? `${months.find(m => m.value === formData.month)?.label} ${formData.year}` : 'June 1975'} • 📍 {formData.location || 'San Francisco, CA'}
              </div>
            </div>

            <div className="flex gap-4 justify-center">
              <button
                onClick={() => {
                  setFormData({ type: '', title: '', month: '', year: '', location: '', customFields: {}, story: '' })
                  setCurrentStep(1)
                }}
                className="px-8 py-4 text-lg font-semibold border-3 border-gray-300 text-[#666666] rounded-xl hover:bg-gray-50 transition-all"
              >
                + Add Another
              </button>
              <button
                onClick={() => navigate('/my-life-v2')}
                className="px-8 py-4 text-lg font-semibold rounded-xl bg-[#556B2F] text-white hover:opacity-90 transition-all"
              >
                View My Timeline →
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  )
}

export { MilestoneWizard }
